<script setup lang="ts">
    import { computed, onMounted, ref, watch } from "vue"
    import { storeToRefs } from "pinia"

    import { Country, Status, CategorySet } from "~/js/interfaces/typesense"
    import { Contact } from "~/js/interfaces/locations"
    import { useLocationsState } from "~/js/stores/locations";
    import { getGlobalTranslation } from "~/js/composables/translations";

    import AlertNoResults from "~/vue/molecules/alerts/AlertNoResults.vue";
    import ButtonPrimary from "~/vue/atoms/buttons/ButtonPrimary.vue";
    import CardLocation from "~/vue/molecules/cards/CardLocation.vue";
    import CardLocationSkeleton from "~/vue/molecules/cards/CardLocationSkeleton.vue";
    import CardContact from "~/vue/molecules/cards/CardContact.vue"
    import FormLocationFilter from "~/vue/organisms/forms/FormLocationFilter.vue";
    import TextH2 from "~/vue/atoms/texts/TextH2.vue";
    import MapLocation from "~/vue/organisms/maps/MapLocation.vue"

    /**
     * INTERFACES
     */
    interface FilterSets {
        statusses: Array<Status | null>;
        countries: Array<Country | null>;
    }

    type Contacts = Array<Contact | null>;

    export interface Props {
        filterSets: FilterSets | null;
        contacts: Contacts | null;
    }

    /**
     * VARIABLES
     * **/
    // props
    const props = defineProps<Props>();
    const ids = {
        'belgium': 1,
        'france': 2,
        'germany': 3,
        'netherlands': 4,
    };

    /**
     * STORE
     */
    const locationsStore = useLocationsState()

    const {
        locations,
        filters, 
        loading,
        pagination 
    } = storeToRefs(locationsStore)

    /**
     * VARIABLES
     */
    const facetSet: Array<CategorySet> = [
        {
            type: "countries",
            countries: props?.filterSets?.countries,
            isFacet: false,
        },
        {
            type: "statusses",
            statusses: props?.filterSets?.statusses,
            isFacet: false,
        },
    ];

    const urlCountries = ref(new URLSearchParams(window.location.search).getAll('countries'))

    /**
     * COMPUTED
     */
    const filteredContacts = computed(() => {
        const countriesParam = urlCountries.value;
        
        if (!countriesParam) return (props.contacts?.filter(contact => contact !== null) || []) as Contact[];
        
        // Flip the ids object to get id -> country mapping
        const countryMap = Object.fromEntries(
            Object.entries(ids).map(([country, id]) => [id, country])
        );
        
        // Get selected countries from URL
        const selectedCountries = countriesParam
            .flatMap(param => param.split(',')) // Handle both formats: multiple params or comma-separated
            .map(id => countryMap[id])
            .filter(Boolean);
        
        // Filter contacts - note: props.contacts.contacts (not props.contacts.value)
        return (props.contacts?.filter(contact => 
            contact !== null && selectedCountries.includes(contact.country)
        ) || []) as Contact[];
    });

    const noResultsMessage = computed(() => {
        let prose = `
            <p>${getGlobalTranslation('No locations were found in this region.')}</p>
            <p>${getGlobalTranslation('Interested in renting a warehouse ? Get in touch with us!')}</p>
        `

        return prose;
    })

    /**
     * METHODS
     **/
    const onSearch = (scrollTo: false) => {
        if (scrollTo) {
            const boundingClientRect = document
                .getElementById("teleport-locations")
                ?.getBoundingClientRect();

            if (boundingClientRect) {
                window.scrollTo({
                    top: boundingClientRect.top + window.scrollY - 110, // Adjusting scroll position
                    behavior: "smooth",
                });
            }
        }

        locationsStore.fetch(true);

         // Update the URL countries ref after search
        urlCountries.value = new URLSearchParams(window.location.search).getAll('countries')
    };

    /**
     * WATCHERS
     **/
    watch(() => filters.value, () => {
        urlCountries.value = new URLSearchParams(window.location.search).getAll('countries')
    }, { deep: true })

    const onLoadMore = () => {
        locationsStore.fetch(false);
    }

    /**
     * LIFECYCLES
     */

    onMounted(async () => {
        let fetchByQueryParameters = true;

        // fetch the locations
        locationsStore.fetch(true, fetchByQueryParameters)
    })
</script>

<template>
    <Teleport to="#teleport-locations">
        <fieldset>
            <legend class="sr-only">
                {{  getGlobalTranslation("Set global filtering for locations") }}
            </legend>

            <div class="flex flex-col md:flex-row md:items-center gap-6 justify-between mb-4">
                <h2>
                    <TextH2>
                        {{ getGlobalTranslation("Where are you interested in doing business?") }}
                    </TextH2>
                </h2>
            </div>

            <FormLocationFilter
                :countries="filterSets?.countries ? Object.values(filterSets.countries) : null"
                :default-countries="filters.countries.values"
                :default-statusses="filters.statusses.values"
                :statusses="filterSets?.statusses ? Object.values(filterSets.statusses) : null"
                @search="onSearch($event)"
                utilities="mb-8"
            />
        </fieldset>

        <!-- location cards -->
        <section class="grid md:grid-cols-2 gap-4 lg:gap-6">
            <div class="grid md:grid-cols-2 gap-4 lg:gap-6 items-start auto-rows-min">
                <template v-if="loading">
                    <CardLocationSkeleton />
                    <CardLocationSkeleton />
                    <CardLocationSkeleton />
                    <CardLocationSkeleton />
                    <CardLocationSkeleton />
                    <CardLocationSkeleton />
                </template>

                <template v-if="locations && !loading">
                    <CardLocation
                        v-for="location in locations.map((location) => location.document)"
                        :key="location.id"
                        :card="location"
                    />

                    <!-- load more -->
                    <div
                        v-if="
                            !loading &&
                            pagination.current < pagination.totalPages &&
                            pagination.current != pagination.totalPages
                        "
                        class="md:col-span-2"
                    >
                        <ButtonPrimary
                            utilities="mx-auto !block mt-4"
                            type="button"
                            :content="getGlobalTranslation('Load more')"
                            @click="onLoadMore"
                        />
                    </div>
                </template>

                <template v-if="locations && locations?.length === 0 && !loading">
                    <!-- no results -->
                    <AlertNoResults
                        :message="noResultsMessage"
                    />

                    <CardContact 
                        v-for="contact in filteredContacts"
                        :key="contact.id"
                        :card="contact"
                    />
                </template>
            </div>

            <template v-if="locations && !loading">
                <MapLocation
                    :locations="locations"
                />
            </template>
        </section>
    </Teleport>
</template>